import { useState, useEffect } from 'react';
import { sortByName } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import { employeesAPI, emailAPI, earningsAPI, locationsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
    Mail,
    Trophy,
    DollarSign,
    Users,
    Send,
    Eye,
    Settings,
    Star,
    TrendingUp,
    Award,
    Clock,
    CheckCircle,
    XCircle,
    Loader2,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';

const ACHIEVEMENT_ICONS = {
    first_review: '🌟',
    review_rookie: '⭐',
    review_pro: '🏅',
    review_rockstar: '🎸',
    top_performer: '🏆',
    consistency_king: '👑'
};

const ACHIEVEMENT_NAMES = {
    first_review: 'First Review',
    review_rookie: 'Review Rookie',
    review_pro: 'Review Pro',
    review_rockstar: 'Review Rockstar',
    top_performer: 'Top Performer',
    consistency_king: 'Consistency King'
};

export default function EmployeeNotifications() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [employees, setEmployees] = useState([]);
    const [locations, setLocations] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [emailSettings, setEmailSettings] = useState({});
    const [previewHtml, setPreviewHtml] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [leaderboardPeriod, setLeaderboardPeriod] = useState('week');
    const [expandedEmployee, setExpandedEmployee] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        loadLeaderboard();
    }, [leaderboardPeriod]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [empRes, locRes] = await Promise.all([
                employeesAPI.list(),
                locationsAPI.list()
            ]);
            setEmployees(sortByName(empRes.data));
            setLocations(sortByName(locRes.data));
            
            // Load email settings for all employees
            const settingsMap = {};
            for (const emp of empRes.data) {
                try {
                    const settingsRes = await emailAPI.getSettings(emp.id);
                    settingsMap[emp.id] = settingsRes.data;
                } catch (e) {
                    settingsMap[emp.id] = { enabled: false, frequency: 'weekly' };
                }
            }
            setEmailSettings(settingsMap);
            
            await loadLeaderboard();
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const loadLeaderboard = async () => {
        try {
            const res = await earningsAPI.getLeaderboard({ period: leaderboardPeriod });
            setLeaderboard(res.data.leaderboard);
        } catch (error) {
            console.error('Failed to load leaderboard:', error);
        }
    };

    const handleToggleEmail = async (employeeId, enabled) => {
        try {
            const currentSettings = emailSettings[employeeId] || {};
            await emailAPI.updateSettings(employeeId, {
                employee_id: employeeId,
                enabled,
                frequency: currentSettings.frequency || 'weekly'
            });
            setEmailSettings(prev => ({
                ...prev,
                [employeeId]: { ...prev[employeeId], enabled }
            }));
            toast.success(enabled ? 'Email notifications enabled' : 'Email notifications disabled');
        } catch (error) {
            toast.error('Failed to update settings');
        }
    };

    const handleFrequencyChange = async (employeeId, frequency) => {
        try {
            const currentSettings = emailSettings[employeeId] || {};
            await emailAPI.updateSettings(employeeId, {
                employee_id: employeeId,
                enabled: currentSettings.enabled ?? true,
                frequency
            });
            setEmailSettings(prev => ({
                ...prev,
                [employeeId]: { ...prev[employeeId], frequency }
            }));
            toast.success('Frequency updated');
        } catch (error) {
            toast.error('Failed to update frequency');
        }
    };

    const handlePreviewEmail = async (employee) => {
        try {
            const res = await emailAPI.preview(employee.id);
            setPreviewHtml(res.data.html);
            setSelectedEmployee(employee);
            setShowPreview(true);
        } catch (error) {
            toast.error('Failed to generate preview');
        }
    };

    const handleSendTestEmail = async (employee) => {
        if (!employee.email) {
            toast.error('Employee does not have an email address');
            return;
        }
        
        setSendingEmail(true);
        try {
            await emailAPI.sendTest(employee.id);
            toast.success(`Test email sent to ${employee.email}`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send email');
        } finally {
            setSendingEmail(false);
        }
    };

    const handleViewEarnings = async (employeeId) => {
        if (expandedEmployee === employeeId) {
            setExpandedEmployee(null);
        } else {
            setExpandedEmployee(employeeId);
        }
    };

    const getLocationName = (locationId) => {
        const loc = locations.find(l => l.id === locationId);
        return loc?.name || 'Unknown';
    };

    const getRankBadge = (rank) => {
        if (rank === 1) return <Badge className="bg-yellow-500">🥇 #1</Badge>;
        if (rank === 2) return <Badge className="bg-gray-400">🥈 #2</Badge>;
        if (rank === 3) return <Badge className="bg-amber-600">🥉 #3</Badge>;
        return <Badge variant="outline">#{rank}</Badge>;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="employee-notifications">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Employee Notifications & Performance</h1>
                    <p className="text-muted-foreground">Manage email notifications, track performance, and view leaderboards</p>
                </div>
            </div>

            <Tabs defaultValue="employees" className="space-y-6">
                <TabsList>
                    <TabsTrigger value="employees" className="flex items-center gap-2">
                        <Mail className="w-4 h-4" /> Email Settings
                    </TabsTrigger>
                    <TabsTrigger value="leaderboard" className="flex items-center gap-2">
                        <Trophy className="w-4 h-4" /> Leaderboard
                    </TabsTrigger>
                </TabsList>

                {/* Email Settings Tab */}
                <TabsContent value="employees">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Mail className="w-5 h-5 text-emerald-500" />
                                Employee Email Settings
                            </CardTitle>
                            <CardDescription>
                                Configure performance report emails for each employee
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {employees.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>{t('employeeNotifications.noEmployeesFound')}</p>
                                </div>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Employee</TableHead>
                                            <TableHead>Email</TableHead>
                                            <TableHead>Location</TableHead>
                                            <TableHead>Notifications</TableHead>
                                            <TableHead>Frequency</TableHead>
                                            <TableHead>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {employees.map(emp => (
                                            <>
                                                <TableRow key={emp.id}>
                                                    <TableCell className="font-medium">
                                                        {emp.name}
                                                    </TableCell>
                                                    <TableCell>
                                                        {emp.email ? (
                                                            <span className="text-muted-foreground">{emp.email}</span>
                                                        ) : (
                                                            <Badge variant="outline" className="text-amber-600">
                                                                No email
                                                            </Badge>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{getLocationName(emp.location_id)}</TableCell>
                                                    <TableCell>
                                                        <Switch
                                                            checked={emailSettings[emp.id]?.enabled || false}
                                                            onCheckedChange={(checked) => handleToggleEmail(emp.id, checked)}
                                                            disabled={!emp.email}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Select
                                                            value={emailSettings[emp.id]?.frequency || 'weekly'}
                                                            onValueChange={(val) => handleFrequencyChange(emp.id, val)}
                                                            disabled={!emailSettings[emp.id]?.enabled}
                                                        >
                                                            <SelectTrigger className="w-28">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="daily">Daily</SelectItem>
                                                                <SelectItem value="weekly">Weekly</SelectItem>
                                                                <SelectItem value="none">None</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => handlePreviewEmail(emp)}
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => handleSendTestEmail(emp)}
                                                                disabled={!emp.email || sendingEmail}
                                                            >
                                                                <Send className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleViewEarnings(emp.id)}
                                                            >
                                                                {expandedEmployee === emp.id ? (
                                                                    <ChevronUp className="w-4 h-4" />
                                                                ) : (
                                                                    <ChevronDown className="w-4 h-4" />
                                                                )}
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                                {expandedEmployee === emp.id && (
                                                    <TableRow>
                                                        <TableCell colSpan={6}>
                                                            <EmployeeEarningsCard employeeId={emp.id} />
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Leaderboard Tab */}
                <TabsContent value="leaderboard">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <Trophy className="w-5 h-5 text-yellow-500" />
                                        Performance Leaderboard
                                    </CardTitle>
                                    <CardDescription>
                                        Top performers ranked by tap count
                                    </CardDescription>
                                </div>
                                <Select value={leaderboardPeriod} onValueChange={setLeaderboardPeriod}>
                                    <SelectTrigger className="w-32">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="week">This Week</SelectItem>
                                        <SelectItem value="month">This Month</SelectItem>
                                        <SelectItem value="all">All Time</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {leaderboard.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Trophy className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>{t('employeeNotifications.noPerformanceData')}</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {/* Top 3 Cards */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                        {leaderboard.slice(0, 3).map((entry, idx) => (
                                            <Card key={entry.employee_id} className={`
                                                ${idx === 0 ? 'border-yellow-400 bg-gradient-to-br from-yellow-50 to-white' : ''}
                                                ${idx === 1 ? 'border-border bg-gradient-to-br from-gray-50 to-white' : ''}
                                                ${idx === 2 ? 'border-amber-400 bg-gradient-to-br from-amber-50 to-white' : ''}
                                            `}>
                                                <CardContent className="pt-6 text-center">
                                                    <div className="text-4xl mb-2">
                                                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}
                                                    </div>
                                                    <h3 className="font-bold text-lg">{entry.employee_name}</h3>
                                                    <p className="text-sm text-muted-foreground">{entry.location_name}</p>
                                                    <div className="mt-4 flex items-center justify-center gap-4">
                                                        <div>
                                                            <p className="text-2xl font-bold text-emerald-600">{entry.tap_count}</p>
                                                            <p className="text-xs text-muted-foreground">{t('employeeNotifications.taps')}</p>
                                                        </div>
                                                        {entry.earnings !== null && (
                                                            <div>
                                                                <p className="text-2xl font-bold text-green-600">${entry.earnings}</p>
                                                                <p className="text-xs text-muted-foreground">{t('employeeNotifications.earned')}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>

                                    {/* Rest of leaderboard */}
                                    {leaderboard.length > 3 && (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Rank</TableHead>
                                                    <TableHead>Employee</TableHead>
                                                    <TableHead>Location</TableHead>
                                                    <TableHead className="text-right">Taps</TableHead>
                                                    <TableHead className="text-right">Earnings</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {leaderboard.slice(3).map(entry => (
                                                    <TableRow key={entry.employee_id}>
                                                        <TableCell>{getRankBadge(entry.rank)}</TableCell>
                                                        <TableCell className="font-medium">{entry.employee_name}</TableCell>
                                                        <TableCell>{entry.location_name}</TableCell>
                                                        <TableCell className="text-right font-bold">{entry.tap_count}</TableCell>
                                                        <TableCell className="text-right">
                                                            {entry.earnings !== null ? `$${entry.earnings}` : '-'}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Email Preview Dialog */}
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Email Preview - {selectedEmployee?.name}</DialogTitle>
                        <DialogDescription>
                            This is how the performance email will look
                        </DialogDescription>
                    </DialogHeader>
                    <div 
                        className="border rounded-lg overflow-hidden"
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                    />
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => setShowPreview(false)}>
                            Close
                        </Button>
                        <Button 
                            onClick={() => {
                                handleSendTestEmail(selectedEmployee);
                                setShowPreview(false);
                            }}
                            disabled={!selectedEmployee?.email || sendingEmail}
                        >
                            <Send className="w-4 h-4 mr-2" />
                            Send Test Email
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Employee Earnings Card Component
function EmployeeEarningsCard({ employeeId }) {
    const { t } = useTranslation();
    const [earnings, setEarnings] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadEarnings();
    }, [employeeId]);

    const loadEarnings = async () => {
        try {
            const res = await earningsAPI.getEmployeeEarnings(employeeId);
            setEarnings(res.data);
        } catch (error) {
            console.error('Failed to load earnings:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
            </div>
        );
    }

    if (!earnings) {
        return <p className="text-muted-foreground py-4">{t('employeeNotifications.failedLoad')}</p>;
    }

    return (
        <div className="bg-muted rounded-lg p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-background p-4 rounded-lg shadow-sm">
                    <p className="text-sm text-muted-foreground">{t('employeeNotifications.thisWeek')}</p>
                    <p className="text-xl font-bold text-emerald-600">{earnings.stats?.reviews_this_week || 0} reviews</p>
                    {earnings.review_value > 0 && (
                        <p className="text-sm text-green-600">${earnings.earnings?.this_week || 0}</p>
                    )}
                </div>
                <div className="bg-background p-4 rounded-lg shadow-sm">
                    <p className="text-sm text-muted-foreground">{t('employeeNotifications.thisMonth')}</p>
                    <p className="text-xl font-bold text-blue-600">{earnings.stats?.reviews_this_month || 0} reviews</p>
                    {earnings.review_value > 0 && (
                        <p className="text-sm text-green-600">${earnings.earnings?.this_month || 0}</p>
                    )}
                </div>
                <div className="bg-background p-4 rounded-lg shadow-sm">
                    <p className="text-sm text-muted-foreground">{t('employeeNotifications.allTime')}</p>
                    <p className="text-xl font-bold text-purple-600">{earnings.stats?.reviews_all_time || 0} reviews</p>
                    {earnings.review_value > 0 && (
                        <p className="text-sm text-green-600">${earnings.earnings?.all_time || 0}</p>
                    )}
                </div>
                <div className="bg-background p-4 rounded-lg shadow-sm">
                    <p className="text-sm text-muted-foreground">{t('employeeNotifications.ranking')}</p>
                    <p className="text-xl font-bold text-yellow-600">#{earnings.stats?.ranking || '-'}</p>
                </div>
            </div>
            
            {earnings.achievements && earnings.achievements.length > 0 && (
                <div className="mt-4">
                    <p className="text-sm text-muted-foreground mb-2">{t('employeeNotifications.achievements')}</p>
                    <div className="flex flex-wrap gap-2">
                        {earnings.achievements.map(achievement => (
                            <Badge key={achievement} variant="secondary" className="bg-amber-100 text-amber-800">
                                {ACHIEVEMENT_ICONS[achievement] || '🏅'} {ACHIEVEMENT_NAMES[achievement] || achievement}
                            </Badge>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
